/**
 * Math card — the expandable per-row verdict arithmetic block (pricing-truth
 * arc Task B, 2026-08-25 — see the arc spec's Web section). Presentation
 * only: every number/label/annotation is pre-computed by
 * lib/order-planning.ts's buildMathCard (table-driven tested there — no
 * component tests, codebase convention). Shared by PlanningPage now and
 * PriceCalibrationPage (Task C) — lives beside OutreachComposer.tsx as a
 * cross-page component, not tucked under a single page's file.
 */
import type { MathCardData } from '../../lib/order-planning';

export function MathCard({
  data,
  variant = 'default',
}: {
  data: MathCardData;
  variant?: 'default' | 'unit-flow';
}) {
  const unitFlow = variant === 'unit-flow';

  return (
    <div
      className={
        unitFlow
          ? 'bg-surface-elev border border-border-subtle rounded-lg px-4 py-3 w-full max-w-2xl'
          : 'bg-surface-elev border border-border-subtle rounded-lg px-4 py-3 max-w-sm'
      }
    >
      <div
        className={
          unitFlow
            ? 'font-mono text-xs grid gap-y-1'
            : 'font-mono text-xs grid grid-cols-[14px_40px_64px_1fr] gap-x-2 gap-y-1 items-baseline'
        }
      >
        {data.lines.map((line) => (
          <div
            key={line.label}
            className={
              unitFlow
                ? 'col-span-full grid grid-cols-[14px_minmax(0,1fr)_minmax(0,64px)] gap-x-2 gap-y-1 items-baseline sm:grid-cols-[14px_minmax(8rem,1fr)_64px_minmax(0,1fr)]'
                : 'contents'
            }
          >
            <span className="text-fg-tertiary">{line.sign}</span>
            <span
              className={
                unitFlow
                  ? 'min-w-0 text-fg-tertiary whitespace-normal break-words [overflow-wrap:anywhere]'
                  : 'text-fg-tertiary whitespace-normal break-words'
              }
            >
              {line.label}
            </span>
            <span
              className={
                unitFlow
                  ? 'min-w-0 text-right tabular-nums text-fg-primary whitespace-normal break-words [overflow-wrap:anywhere]'
                  : 'text-right tabular-nums text-fg-primary'
              }
            >
              {line.amount}
            </span>
            <span
              className={
                unitFlow
                  ? 'col-start-2 col-span-2 min-w-0 text-fg-tertiary whitespace-normal break-words [overflow-wrap:anywhere] sm:col-start-4 sm:col-span-1'
                  : 'text-fg-tertiary whitespace-normal'
              }
            >
              {line.annotation}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-2.5 pt-2.5 border-t border-border-subtle space-y-1 text-xs text-fg-tertiary">
        {data.thresholdsLine && <p>{data.thresholdsLine}</p>}
        {data.trendWarningLine && <p className="text-warning">{data.trendWarningLine}</p>}
        {data.buyBoxLine && <p>{data.buyBoxLine}</p>}
        {data.authorizationLine && <p className="text-warning">{data.authorizationLine}</p>}
        <p className="text-fg-primary font-medium">{data.verdictLine}</p>
      </div>
    </div>
  );
}
